<template>
  <div class="inline-block w-full">
    <div v-if="label" class="mb-1">
      <label
        :for="label"
        class="text-text-primary text-sm font-medium dark:text-dark-secondary"
        :class="[labelClass, allowLabelWrap ? '' : 'label-wrapper']"
      >
        {{ label }}
        <span v-if="required" class="ml-1">
          <span v-if="!requiredMin">(Obligatorio)</span>
          <span class="text-text-error">*</span>
        </span>
      </label>
    </div>

    <Select
      v-model="model"
      :options="orderedOptions"
      :optionLabel="optionLabel"
      :placeholder="placeholder"
      :optionValue="optionValue"
      :optionGroupLabel="optionGroupLabel"
      :optionGroupChildren="optionGroupChildren"
      :filter="filter"
      :filterFields="filterFields"
      :disabled="disabled"
      class="w-full dark:bg-surface-primary! rounded-sm!"
      :class="[
        selectClass,
        { 'border-helper-text!': error, 'custom-select-disabled': disabled },
      ]"
      @change="emits('change', $event)"
    >
      <template #optiongroup="slotProps">
        <div
          class="flex items-center font-bold text-[14px] ml-1 text-text-secondary!"
        >
          {{ slotProps.option.label }}
        </div>
      </template>

      <template #value="slotProps">
        <div
          v-if="slotProps.value !== null && slotProps.value !== undefined"
          class="flex items-center"
        >
          <component
            :is="renderOption(slotProps.value, 'value')"
            :data="slotProps.value"
          />
        </div>
        <span v-else>{{ placeholder }}</span>
      </template>

      <template #option="slotProps">
        <div class="flex items-center">
          <component
            :is="renderOption(slotProps.option, 'option')"
            :data="slotProps.option"
          />
        </div>
      </template>
    </Select>

    <div v-if="error" class="relative h-4 mt-1">
      <transition name="fade">
        <p
          class="absolute text-helper-text text-[11px] flex items-center gap-1"
        >
          <font-awesome-icon icon="fa-solid fa-circle-xmark" />
          {{ error }}
        </p>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { computed, h } from "vue";
import { rutFormatter } from "../../utils/rutFormatter";

const model = defineModel();
const props = defineProps({
  options: { type: Array, default: () => [] },
  optionLabel: String,
  valueLabel: String,
  optionValue: String,
  optionGroupLabel: String,
  optionGroupChildren: String,
  placeholder: { type: String, default: "Seleccione una opción" },
  selectClass: { type: String, default: "font-400 text-[14px]" },
  isDescriptionStyle: Boolean,
  labelClass: String,
  label: String,
  required: { type: Boolean, default: true },
  requiredMin: Boolean,
  filter: Boolean,
  filterFields: Array,
  disabled: Boolean,
  customOption: Boolean,
  error: String,
  allowLabelWrap: Boolean,
  favoriteClass: String,
  isAccountType: Boolean,
  isAccountCredicorp: Boolean,
  isBeneficiaryList: Boolean,
  currencyIcon: Boolean,
  isTableSelect: Boolean,
});

const emits = defineEmits(["change"]);

// Configuración de banderas
const FLAGS = {
  1: {
    src: new URL("../../assets/flags/clp.svg", import.meta.url).href,
  },
  2: {
    src: new URL("../../assets/flags/usd.svg", import.meta.url).href,
  },
  3: {
    src: new URL("../../assets/flags/eur.svg", import.meta.url).href,
  },
};

// Lógica de Ordenamiento
const orderedOptions = computed(() => {
  if (!props.currencyIcon || !Array.isArray(props.options))
    return props.options;

  return props.options;
});

// Renderizador Dinámico (Reemplaza los v-if gigantes)
// Renderizador Dinámico
const renderOption = (item, context = 'option') => {
  return () => {
    // 1. Si PrimeVue nos tira solo el ID primitivo (ej: 7), buscamos el objeto completo
    let option = item;
    if (item !== null && typeof item !== "object") {
      const key = props.optionValue || "id";
      option = props.options.find((o) => o[key] === item) || item;
    }

    // Si por alguna razón sigue sin ser objeto (ej: valor por defecto raro), tiramos el texto crudo y chao
    if (typeof option !== "object") {
      return h("span", String(option));
    }

    const children = [];

    // 2. Iconos o Estrellas (Favoritos)
    if (props.customOption && option.favorite) {
      children.push(
        h("span", { class: "w-5 inline-block text-center" }, [
          h("font-awesome-icon", {
            icon: "fa-solid fa-star",
            class: props.favoriteClass,
          }),
        ]),
      );
    }

    // 3. Imagen/Bandera
    const flagSrc = props.currencyIcon ? FLAGS[option.id]?.src : option.src;
    if (flagSrc) {
      children.push(h("img", { src: flagSrc, class: "mr-2 w-4" }));
    }

    // 4. Iconos de FontAwesome (por si los usas en otros selects)
    if (option.icon) {
      children.push(
        h("font-awesome-icon", {
          icon: option.icon,
          class: `mr-2 ${option.iconClass || ""}`,
        }),
      );
    }

    // 5. Contenido Principal
    let text = "";

    if (props.isBeneficiaryList && option.beneficiary) {
      text = `${option.beneficiary.name} - ${rutFormatter(option.beneficiary.rut)}`;
    } else if (props.isAccountCredicorp) {
      text = `${option.description} - ${option.accountNumber}`;
    } else if (props.isAccountType) {
      const bName = option.beneficiary?.name
        ? `${option.beneficiary.name} - `
        : "";
      text = `${bName}${option.account || ""} - ${option.name || ""}`;
    } else if (props.optionLabel && option[props.optionLabel]) {
      // Aquí engancha tu "valor"
      text = context === 'value' && props.valueLabel && option[props.valueLabel] 
        ? option[props.valueLabel] 
        : option[props.optionLabel];
    } else {
      // Fallbacks por si acaso
      text =
        option.label ||
        option.description ||
        option.value ||
        option.valor ||
        "";
    }

    children.push(
      h("span", { class: props.isTableSelect ? "text-[10px]" : "" }, text),
    );

    return h("div", { class: "flex items-center" }, children);
  };
};
</script>

<style scoped>
.custom-select-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f3f4f6;
  color: #9ca3af;
}

.custom-select-disabled:hover {
  cursor: not-allowed;
}

/* Estilos específicos para el dropdown de PrimeVue cuando está disabled */
:deep(.p-select.p-disabled) {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f3f4f6 !important;
  color: #9ca3af !important;
}

:deep(.p-select.p-disabled .p-select-label) {
  color: #9ca3af !important;
}

:deep(.p-select.p-disabled .p-select-dropdown) {
  cursor: not-allowed;
}
</style>
